(() => {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function getProductRoot(section) {
    return section ? section.querySelector('.js-product') : null;
  }

  function getMainCurrentPriceEl(product) {
    if (!product) return null;
    let el = product.querySelector('.product-info__price .price .price__current.tpo_product_price');
    if (!el) {
      el = product.querySelector('.product-info__price .price .price__current .js-value');
    }
    if (!el) {
      el = product.querySelector('.product-info__price .price .price__current');
    }
    return el;
  }

  function getPrimaryPriceRoot(product) {
    if (!product) return null;
    const current = getMainCurrentPriceEl(product);
    if (current) {
      const root = current.closest('.price');
      if (root) return root;
    }
    return product.querySelector('.product-info__price > .price');
  }

  /**
   * Compare-at for sticky sync. Easify sets `tpo_product_old_price` (often a *second* sibling
   * `.price__was` after a hidden legacy row). Always prefer that class; never rely on the first
   * `.price__was` only.
   */
  function getMainCompareAtPriceText(product) {
    if (!product) return '';

    const fromEasify = product.querySelector(
      '.product-info__price .tpo_product_old_price, .product-info__price .price__was.tpo_product_old_price'
    );
    if (fromEasify) {
      const t = fromEasify.textContent.trim();
      if (t) return t;
    }

    const priceRoot = getPrimaryPriceRoot(product);
    if (!priceRoot) return '';

    const wasSpan = priceRoot.querySelector('.price__was');
    if (!wasSpan) return '';

    const jsValueEl = wasSpan.querySelector('.js-value');
    if (jsValueEl) {
      const fromJsValue = jsValueEl.textContent.trim();
      if (fromJsValue) return fromJsValue;
    }

    const clone = wasSpan.cloneNode(true);
    clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
    const synthesized = clone.textContent.replace(/\s+/g, ' ').trim();
    return synthesized || '';
  }

  function syncStickyPrice(panel, product) {
    const currentEl = panel.querySelector('[data-sticky-atc-price-current]');
    const compareWrap = panel.querySelector('[data-sticky-atc-price-compare-wrap]');
    const compareEl = panel.querySelector('[data-sticky-atc-price-compare]');
    if (!currentEl || !product) return;

    const mainCurrent = getMainCurrentPriceEl(product);
    if (mainCurrent) {
      currentEl.textContent = mainCurrent.textContent.trim();
    }

    if (!compareWrap || !compareEl) return;

    const wasText = getMainCompareAtPriceText(product);

    if (wasText) {
      compareEl.textContent = wasText;
      compareWrap.hidden = false;
    } else {
      compareEl.textContent = '';
      compareWrap.hidden = true;
    }
  }

  function getMainAddButton(section) {
    if (!section) return null;
    const form = section.querySelector('.js-product-form-main');
    return form ? form.querySelector('[name="add"]') : null;
  }

  function getNoSelectedVariantString() {
    return (window.theme && theme.strings && theme.strings.noSelectedVariant) || '';
  }

  function isPreSelectionState(mainBtn) {
    if (!mainBtn) return false;
    const noSelected = getNoSelectedVariantString().trim();
    if (!noSelected) return false;
    return mainBtn.textContent.trim() === noSelected;
  }

  function getVariantsAnchor(section) {
    return (
      section.querySelector('.product-options--anchor#variants') ||
      section.querySelector('#variants') ||
      section.querySelector('.product-options--anchor') ||
      section.querySelector('variant-picker')
    );
  }

  function scrollToVariants(section) {
    const anchor = getVariantsAnchor(section);
    if (!anchor) return;
    anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const variantPicker = section.querySelector('variant-picker');
    if (variantPicker) {
      const firstOption = variantPicker.querySelector('input:not([type="hidden"]), button, select');
      if (firstOption && typeof firstOption.focus === 'function') {
        setTimeout(() => {
          try {
            firstOption.focus({ preventScroll: true });
          } catch {
            firstOption.focus();
          }
        }, 350);
      }
    }
  }

  function isChromeMobile() {
    const ua = navigator.userAgent || '';
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    const isChrome = /CriOS|Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua);
    return isMobile && isChrome;
  }

  function mirrorMainButtonToSticky(stickyBtn, mainBtn) {
    if (!stickyBtn || !mainBtn) return;

    const label = mainBtn.textContent.trim();
    if (label) stickyBtn.textContent = label;

    // Pre-selection state: keep the sticky CTA clickable so it can scroll to
    // the variant picker (preserves the original "Choose options" UX).
    if (isPreSelectionState(mainBtn)) {
      stickyBtn.disabled = false;
      stickyBtn.removeAttribute('aria-disabled');
    } else {
      const busy = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
      stickyBtn.disabled = busy;
      if (busy) {
        stickyBtn.setAttribute('aria-disabled', 'true');
      } else {
        stickyBtn.removeAttribute('aria-disabled');
      }
    }

    stickyBtn.classList.toggle('is-loading', mainBtn.classList.contains('is-loading'));
    stickyBtn.classList.toggle('is-success', mainBtn.classList.contains('is-success'));
  }

  function bindStickyAtcPanel(panel) {
    if (panel.__mStickyAtcBound) return;
    panel.__mStickyAtcBound = true;
    panel.classList.toggle('sticky-atc-panel--chrome-mobile', isChromeMobile());

    const section = panel.closest('.cc-main-product');
    if (!section) return;

    const product = getProductRoot(section);
    const stickyBtn = panel.querySelector('.sticky-atc-panel__cta');
    // Enterprise 2.3.0 replaces the add-to-cart row (and its button) on every
    // variant change, so always look the main button up fresh.
    const mainForm = section.querySelector('.js-product-form-main');
    const productForm = section.querySelector('product-form');
    const productInfo = section.querySelector('.product-info');

    const runPrice = () => syncStickyPrice(panel, product);
    const runBtn = () => mirrorMainButtonToSticky(stickyBtn, getMainAddButton(section));
    const debouncedPrice = debounce(runPrice, 120);
    let visibilityFrame = null;
    const runVisibility = () => {
      if (!productForm) return;
      panel.classList.toggle(
        'sticky-atc-panel--out',
        productForm.getBoundingClientRect().bottom > 0
      );
    };
    const scheduleVisibility = () => {
      if (visibilityFrame) cancelAnimationFrame(visibilityFrame);
      visibilityFrame = requestAnimationFrame(() => {
        visibilityFrame = null;
        runVisibility();
      });
    };

    runPrice();
    runBtn();
    runVisibility();

    if (productForm && 'IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(scheduleVisibility);
      visibilityObserver.observe(productForm);
      if (productInfo) visibilityObserver.observe(productInfo);

      window.addEventListener('on:breakpoint-change', scheduleVisibility);
    }

    if (product) {
      product.addEventListener('on:product-price:change', debouncedPrice);

      // The variant picker and price row are replaced on every variant change:
      // listen on the product and observe the stable price block.
      product.addEventListener('on:variant:change', () => {
        debouncedPrice();
        runBtn();
      });

      const priceEl = product.querySelector('.product-info__price');
      const priceRoot = priceEl ? priceEl.closest('.product-price') || priceEl : null;
      if (priceRoot && 'MutationObserver' in window) {
        const moPrice = new MutationObserver(() => debouncedPrice());
        moPrice.observe(priceRoot, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    if (mainForm && 'MutationObserver' in window) {
      const moBtn = new MutationObserver(runBtn);
      moBtn.observe(mainForm, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // The sticky CTA is a form-attached submit button targeting the main
    // product form, so the browser handles submission natively. We only
    // intervene for two UX states:
    //   1. Pre-selection — scroll to the variant picker instead of submitting.
    //   2. Sold out / unavailable — block the submit defensively (the
    //      mirrored disabled state already prevents it in normal browsers).
    if (stickyBtn && mainForm) {
      stickyBtn.addEventListener('click', (evt) => {
        const mainBtn = getMainAddButton(section);
        if (!mainBtn) return;

        if (isPreSelectionState(mainBtn)) {
          evt.preventDefault();
          scrollToVariants(section);
          return;
        }

        if (mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true') {
          evt.preventDefault();
        }
      });
    }
  }

  function init() {
    document.querySelectorAll('sticky-atc-panel.sticky-atc-panel').forEach(bindStickyAtcPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
